import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";
import SignalCurrentList from "../SignalCurrentList";
import SignalCurrentSummary from "../SignalCurrentList/SignalCurrentSummary";
import SignalModifiedList from "../SignalModifiedList";
import SignalModifiedSummary from "../SignalModifiedList/SignalModifiedSummary";
import DifferenceSection from "../DifferenceSection";
import SignalDifferenceSummary from "../DifferenceSection/SignalDifferenceSummary";
import { extractTickerAndWeight, hashInvestment, normalizeTickerForComparison } from "../../../component/Methods";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { selectNewsData } from "../../../Redux/Reducers/strategiesSlice";
import { addActiveRequests, selectActiveRequests } from "../../../Redux/Reducers/uiSlice";


const SignalsDisplay = ({
  strategies,
  strategyValObj,
  strategyVal,
  pasted,
  setPasted,
  setPasteButton,
  setVerifyEnabled,
  setCopiedData,
  copiedData,
  setHandleVerify,
  setSaveChangeHandler,
  setNumberOfInvalidObjects,
  setEmpty,
  setError,
  error,
  empty,
 }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [modifiedSignalsData, setModifiedSignalsData] = useState([]);
  const [currentSignalsData, setCurrentSignalsData] = useState([]);
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [differenceCopyMenuAnchor, setDifferenceCopyMenuAnchor] = useState(null);
  const [differenceCopyMenuOpen, setDifferenceCopyMenuOpen] = useState(false);
  const [isLoadingNews, setIsLoadingNews] = useState(false);
  const [hasFetchedNewsForStrategy, setHasFetchedNewsForStrategy] = useState(false);

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const closeCopyMenu = () => {
    blurActiveElement();
    setCopyMenuOpen(false);
  };

  const closeDifferenceCopyMenu = () => {
    blurActiveElement();
    setDifferenceCopyMenuOpen(false);
  };
  
  const newsData = useSelector(selectNewsData);
  const activeRequests = useSelector(selectActiveRequests);
  const isNewsRequestActive = activeRequests.includes("get_news");

  const fetchNewsData = () => {
    if (!strategyValObj) return;
    
    setIsLoadingNews(true);
    const request = {
      action: "get_news",
      data: {
        strategy: strategyValObj,
      },
    };
    dispatch(addActiveRequests("get_news"));
    sendWebSocketMessage(request);
  };

  // Reset news state when strategy changes
  useEffect(() => {
    setIsLoadingNews(false);
    setHasFetchedNewsForStrategy(false);
  }, [strategyValObj]);

  // Update loading state when request completes (activeRequests removes "get_news")
  useEffect(() => {
    if (isLoadingNews && !isNewsRequestActive) {
      // Request completed
      setIsLoadingNews(false);
      setHasFetchedNewsForStrategy(true);
    }
  }, [isNewsRequestActive, isLoadingNews]);

  // Extract current signals data from strategies
  useEffect(() => {
    if (strategyValObj && strategies && strategyVal) {
      const strategy = strategies.find(
        (strategy) => strategy.strategyId === strategyValObj
      );

      if (strategy && strategy.signals) {
        const { weights_keys, weights_values } = strategy.signals;

        const keys = Object.keys(weights_keys);
        const combinedArray = keys.map((key) => {
          const investment = weights_keys[key]?.investment;
          const symbol = weights_keys[key]?.symbol;
          const stock = weights_keys[key]?.stock;
          const name = stock ? `${symbol}/${stock}` : symbol;

          return {
            key,
            investment: investment,
            name: name?.toUpperCase() || "",
            value: (weights_values[key] * 100)?.toFixed(4),
            long_short: weights_values[key] >= 0 ? "L" : "S",
            stock: stock,
            assetType: hashInvestment(investment) || "Equity",
          };
        });

        setCurrentSignalsData(combinedArray);
      } else {
        setCurrentSignalsData([]);
      }
    } else {
      setCurrentSignalsData([]);
    }
  }, [strategies, strategyValObj, strategyVal]);

  const reset = () => {
    setVerifyEnabled(false);
    setCopiedData([]);
    setNumberOfInvalidObjects(0);
    setEmpty(false);
    setError("");
  };

  const pasteFromClipboard = async () => {
    try {
      const entries = await navigator.clipboard.readText();

      // Try JSON first (fastest path)
      try {
        const result = JSON.parse(entries);
        if (
          Array.isArray(result) &&
          result.every((obj) => typeof obj === "object")
        ) {
          setPasted(result);
          reset();
          return;
        }
      } catch (jsonParseError) {
        // Continue to text processing
      }

      // Process as text - normalize tabs to spaces so extractTickerAndWeight can handle both formats
      const lines = entries.split("\n").filter((entry) => entry.trim() !== "");
      const results = [];

      for (let i = 0; i < lines.length; i++) {
        // Normalize tabs to spaces for consistent parsing
        const normalizedLine = lines[i].replace(/\t/g, " ");
        const extracted = extractTickerAndWeight(normalizedLine);
        
        if (extracted) {
          // Find matching signal from currentSignalsData to get assetType
          // Use normalizeTickerForComparison to match tickers with '.' or '/' separators
          const extractedNormalized = normalizeTickerForComparison(extracted.name);
          const currentSignal = currentSignalsData.find(
            (sig) => normalizeTickerForComparison(sig.name) === extractedNormalized
          );
          
          // Use assetType from currentSignal or derive from investment, default to Equity
          const assetType = currentSignal?.assetType || 
                           (currentSignal?.investment !== undefined ? hashInvestment(currentSignal.investment) : null) || 
                           "Equity";
          
          results.push({
            ...extracted,
            long_short: parseFloat(extracted.value) >= 0 ? "L" : "S",
            assetType: assetType,
          });
        }
      }

      setPasted(results);
      reset();

      if (results.length > 0) {
        toast.success(`Processed ${results.length} entries`);
      }
    } catch (err) {
      console.error("Paste error:", err);
      setPasted([]);
      toast.error("Error: Cannot paste from clipboard");
    }
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Summary Tables Section */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }} sx={{minHeight: "220px"}}>
            <Box
              sx={{
                height: '100%',
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "1.1rem",
                }}
              >
                Signals Summary
                {strategyValObj &&
                  strategies &&
                  (() => {
                    const strategy = strategies.find(
                      (s) => s.strategyId === strategyValObj
                    );
                    const signalsDate = strategy?.signals?.date;
                    if (signalsDate) {
                      const formattedDate = new Date(
                        signalsDate
                      ).toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                      return (
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 400,
                            fontSize: "0.9rem",
                            color: theme.palette.text.secondary,
                            ml: 1,
                          }}
                        >
                          - Updated: {formattedDate}
                        </Typography>
                      );
                    }
                    return null;
                  })()}
              </Typography>
              <SignalCurrentSummary
                strategies={strategies}
                strategyVal={strategyValObj}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{minHeight: "220px"}}>
            <Box
              sx={{
                height: "100%",
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "1.1rem",
                }}
              >
                Modified Signals Summary
              </Typography>
              <SignalModifiedSummary
                strategies={strategies}
                strategyVal={strategyValObj}
                modifiedSignalsData={modifiedSignalsData}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{minHeight: "220px"}}>
            <Box
              sx={{
                height: "100%",
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "1.1rem",
                }}
              >
                Difference Summary
              </Typography>
              <SignalDifferenceSummary
                currentSignalsData={currentSignalsData}
                modifiedSignalsData={modifiedSignalsData}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Signal Data Section */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{flex: 1, minHeight: 0, display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" }}}>
          {/* Current List */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, height: { xs: "400px", md: "auto" } }}>
            <Box
              sx={{
                flex: {md: 1},
                minHeight: 0,
                height: {xs: "600px"},
                display: "flex",
                flexDirection: "column",
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: "1.1rem",
                    mb: 2,
                  }}
                >
                  Current Signals
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {!hasFetchedNewsForStrategy && (
                    <Tooltip title="Fetch News Data" arrow placement="top">
                      <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<NewspaperIcon />}
                          onClick={fetchNewsData}
                          disabled={isLoadingNews || !strategyValObj}
                          sx={{
                            backgroundColor: theme.palette.info.main,
                            color: "white",
                            fontWeight: 500,
                            "&:hover": {
                              backgroundColor: theme.palette.info.dark,
                              transform: "scale(1.02)",
                            },
                            "&:disabled": {
                              backgroundColor: theme.palette.grey[400],
                              color: theme.palette.grey[600],
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {isLoadingNews ? "Loading..." : "Fetch News"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  <Tooltip title="Copy Signals" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={(event) => {
                        setCopyMenuAnchor(event.currentTarget);
                        setCopyMenuOpen(true);
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        fontWeight: 500,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: theme.palette.primary.light + "20",
                          transform: "scale(1.02)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {/* Copy Menu */}
              <Menu
                anchorEl={copyMenuAnchor}
                open={copyMenuOpen}
                onClose={closeCopyMenu}
                disableRestoreFocus
                closeAfterTransition={false}
                MenuListProps={{
                  "aria-labelledby": "copy-button",
                }}
              >
                <MenuItem
                  onClick={() => {
                    // Copy for Website functionality
                    
                    const currentListData = document.querySelector(
                      '[data-testid="current-list-data"]'
                    );

                    if (currentListData) {
                      
                      navigator.clipboard.writeText(
                        currentListData.dataset.jsonData || "[]"
                      );
                      setPasteButton(true);
                      toast.success("Copied signals data for Website!");
                    } else {
                      toast.error("No data available to copy");
                    }
                    closeCopyMenu();
                  }}
                >
                  Copy for Website
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    // Copy for Excel functionality
                    const currentListData = document.querySelector(
                      '[data-testid="current-list-data"]'
                    );
                    if (currentListData) {
                      const data = JSON.parse(
                        currentListData.dataset.jsonData || "[]"
                      );
                      let formattedData = data
                        .map((item) => `${item.name}\t${item.value}%`)
                        .join("\n");
                      navigator.clipboard.writeText(formattedData);
                      toast.success("Copied signals data for Excel!");
                    } else {
                      toast.error("No data available to copy");
                    }
                    closeCopyMenu();
                  }}
                >
                  Copy for Excel
                </MenuItem>
              </Menu>

              {/* Difference Copy Menu */}
              <Menu
                anchorEl={differenceCopyMenuAnchor}
                open={differenceCopyMenuOpen}
                onClose={closeDifferenceCopyMenu}
                disableRestoreFocus
                closeAfterTransition={false}
                MenuListProps={{
                  "aria-labelledby": "difference-copy-button",
                }}
              >
                <MenuItem
                  onClick={() => {
                    // Copy for Excel functionality for difference data
                    if (modifiedSignalsData.length > 0) {
                      // Get the difference data by calling the logic from DifferenceSection
                      const currentMap = new Map();
                      const modifiedMap = new Map();

                      // Build current signals map
                      currentSignalsData.forEach(signal => {
                        const ticker = normalizeTickerForComparison(signal.name);
                        currentMap.set(ticker, {
                          ...signal,
                          ticker,
                          name: signal.name,
                          value: parseFloat(signal.value),
                          long_short: signal.long_short,
                          assetType: signal.assetType || hashInvestment(signal.investment),
                        });
                      });

                      // Build modified signals map
                      modifiedSignalsData.forEach(signal => {
                        const ticker = normalizeTickerForComparison(signal.name);
                        modifiedMap.set(ticker, {
                          ...signal,
                          ticker,
                          name: signal.name.toUpperCase(),
                          value: parseFloat(signal.value),
                          long_short: signal.value >= 0 ? "L" : "S",
                          assetType: hashInvestment(signal.investment),
                        });
                      });

                      // Find all unique tickers from both sets
                      const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

                      // Build difference data - show ALL signals
                      const differences = [];

                      allTickers.forEach(ticker => {
                        const currentSignal = currentMap.get(ticker);
                        const modifiedSignal = modifiedMap.get(ticker);

                        let changeType = "UNCHANGED";
                        let displayData = null;

                        if (currentSignal && modifiedSignal) {
                          // Ticker exists in both - check if values changed
                          const currentValue = currentSignal.value;
                          const modifiedValue = modifiedSignal.value;
                          const currentAssetType = currentSignal.assetType;
                          const modifiedAssetType = modifiedSignal.assetType;

                          // Check if any key attributes changed
                          const valueChanged = Math.abs(currentValue - modifiedValue) > 0.0001;
                          const assetTypeChanged = currentAssetType !== modifiedAssetType;
                          const longShortChanged = currentSignal.long_short !== modifiedSignal.long_short;

                          if (valueChanged || assetTypeChanged || longShortChanged) {
                            changeType = "CHANGED";
                          }

                          displayData = { ...modifiedSignal };
                        } else if (!currentSignal && modifiedSignal) {
                          // Only in modified signals
                          changeType = "OPEN";
                          displayData = { ...modifiedSignal };
                        } else if (currentSignal && !modifiedSignal) {
                          // Only in current signals
                          changeType = "CLOSED";
                          displayData = { ...currentSignal };
                        }

                        if (displayData) {
                          differences.push({
                            ...displayData,
                            change: changeType,
                          });
                        }
                      });

                      // Sort by ticker name
                      differences.sort((a, b) => a.ticker.localeCompare(b.ticker));

                      // Format for Excel (tab-separated)
                      let formattedData = differences
                        .map((item) => `${item.name}\t${item.value}%\t${item.long_short}\t${item.assetType}\t${item.change}`)
                        .join("\n");

                      navigator.clipboard.writeText(formattedData);
                      toast.success("Copied difference data for Excel!");
                    } else {
                      toast.error("No difference data available to copy");
                    }
                    closeDifferenceCopyMenu();
                  }}
                >
                  Copy for Excel
                </MenuItem>
              </Menu>

              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SignalCurrentList
                  strategies={strategies}
                  strategyValObj={strategyValObj}
                  strategyVal={strategyVal}
                  setPasteButton={setPasteButton}
                  onCopyClick={closeCopyMenu}
                  isLoadingNews={isLoadingNews}
                  setIsLoadingNews={setIsLoadingNews}
                  hasFetchedNews={hasFetchedNewsForStrategy}
                  fetchNewsData={fetchNewsData}
                  newsData={newsData}
                />
              </Box>
            </Box>
          </Box>

          {/* Signal Ticker Data */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box
              sx={{
                flex: {md: 1},
                minHeight: 0,
                height: {xs: "600px"},
                display: "flex",
                flexDirection: "column",
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: "1.1rem",
                    mb: 2,
                  }}
                >
                  Modified Signals
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Tooltip title="Clear all signals" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={() => {
                        // Only trigger the clear confirmation popup
                        // Don't clear data until user confirms
                        const clearButton = document.querySelector(
                          '[data-testid="clear-confirmation-trigger"]'
                        );
                        if (clearButton) {
                          clearButton.click();
                        }
                      }}
                      sx={{
                        color: theme.palette.error.main,
                        borderColor: theme.palette.error.main,
                        fontWeight: 500,
                        boxShadow:
                          theme.palette.mode === "light"
                            ? "0 1px 3px rgba(0,0,0,0.1)"
                            : "none",
                        "&:hover": {
                          borderColor: theme.palette.error.dark,
                          backgroundColor: theme.palette.error.light + "20",
                          boxShadow:
                            theme.palette.mode === "light"
                              ? "0 2px 6px rgba(0,0,0,0.15)"
                              : "none",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Clear
                    </Button>
                  </Tooltip>
                  <Tooltip title="Paste from clipboard" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentPasteIcon />}
                      onClick={pasteFromClipboard}
                      sx={{
                        color: theme.palette.success.main,
                        borderColor: theme.palette.success.main,
                        fontWeight: 500,
                        boxShadow:
                          theme.palette.mode === "light"
                            ? "0 1px 3px rgba(0,0,0,0.1)"
                            : "none",
                        "&:hover": {
                          borderColor: theme.palette.success.dark,
                          backgroundColor: theme.palette.success.light + "20",
                          boxShadow:
                            theme.palette.mode === "light"
                              ? "0 2px 6px rgba(0,0,0,0.15)"
                              : "none",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Paste
                    </Button>
                  </Tooltip>
                  <Tooltip title="Add new signal" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        // Add Signal functionality - trigger the add row function
                        const addButton = document.querySelector(
                          '[data-testid="add-signal-trigger"]'
                        );
                        if (addButton) {
                          addButton.click();
                        }
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        fontWeight: 500,
                        boxShadow:
                          theme.palette.mode === "light"
                            ? "0 1px 3px rgba(0,0,0,0.1)"
                            : "none",
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: theme.palette.primary.light + "20",
                          boxShadow:
                            theme.palette.mode === "light"
                              ? "0 2px 6px rgba(0,0,0,0.15)"
                              : "none",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Add Signal
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <SignalModifiedList
                  pastedArray={pasted}
                  setpasted={setPasted}
                  strategyValObj={strategyValObj}
                  setVerifyEnabled={setVerifyEnabled}
                  setCopiedData={setCopiedData}
                  setHandleVerify={setHandleVerify}
                  setSaveChangeHandler={setSaveChangeHandler}
                  setNumberOfInvalidObjects={setNumberOfInvalidObjects}
                  setEmpty={setEmpty}
                  setError={setError}
                  error={error}
                  empty={empty}
                  onDataChange={setModifiedSignalsData}
                />
              </Box>
            </Box>
          </Box>

          {/* Difference Section */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box
              sx={{
                flex: {md: 1},
                minHeight: 0,
                height: {xs: "600px"},
                display: "flex",
                flexDirection: "column",
                border:
                  theme.palette.mode === "light"
                    ? `2px solid ${theme.palette.grey[200]}`
                    : `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                p: 3,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "#ffffff"
                    : theme.palette.background.paper,
                transition: "all 0.2s ease-in-out",
                "&:hover":
                  theme.palette.mode === "light"
                    ? {
                        borderColor: theme.palette.grey[300],
                        backgroundColor: "#fafbfc",
                      }
                    : {},
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: "1.1rem",
                    mb: 2,
                  }}
                >
                  Difference
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Tooltip title="Copy Difference Data" arrow placement="top">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={(event) => {
                        setDifferenceCopyMenuAnchor(event.currentTarget);
                        setDifferenceCopyMenuOpen(true);
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        fontWeight: 500,
                        "&:hover": {
                          borderColor: theme.palette.primary.dark,
                          backgroundColor: theme.palette.primary.light + "20",
                          transform: "scale(1.02)",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ flex: 1, minHeight: 0 }}>
                <DifferenceSection
                  currentSignals={currentSignalsData}
                  modifiedSignalsData={modifiedSignalsData}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignalsDisplay;
